import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { first, difference } from 'lodash-es';
import { Accounting } from 'anchor-scout-common/types';
import { Drawer, Button, Callout, Spinner, Typography, useDidUpdate, useStateFromUrl, ConfirmationModal, Row } from 'seagull';
import AttachFiles from '~/components/AttachFiles/AttachFiles';
import DisabledBillingTooltip from '~/components/DisabledBillingTooltip';
import EmptyState from '~/components/EmptyState/EmptyState';
import GenericError from '~/components/GenericError/GenericError';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { useOverlayContext, useIoContext, useStoreActions, useStore } from '~/contexts';
import { useAccountingItems } from '~/hooks/reactQuery/queries/useAccountingItems';
import { useQboClasses } from '~/hooks/reactQuery/queries/useQboClasses';
import { useQboTimeActivities } from '~/hooks/reactQuery/queries/useQboTimeActivities';
import { useAttachFiles } from '~/hooks/useAttachFiles';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { RestfulStrategy, useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { timeModel, BillingControlDataModel, relationshipModel } from '~/models';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import { selectors } from '~/store/state-selectors';
import { formatNumericUSDate } from '~/utils';
import MinisiteTabHeader from '../MinisiteTabHeader/MinisiteTabHeader';
import BillingControl from './BillingControl/BillingControl';
import BillingControlFooter from './BillingControl/BillingControlFooter';
import styles from './BillingHub.module.scss';
import { BillingHubUrlParams, getEmptyState, getInvoiceDateInfo, getLineItemsProps, getNoInvoicesText } from './BillingHub.utils';
import InvoiceNavigator from './InvoiceNavigator/InvoiceNavigator';
import ScheduledInvoice from './ScheduledInvoice/ScheduledInvoice';
function BillingHubWithData(props) {
    const { agreement, billingData, isVendor, onRefresh: refreshBillingHubData } = props;
    const { invoiceDraftDates, billingControlData } = billingData;
    const { api, logBI } = useStoreActions();
    const { getNow } = useIoContext();
    const { DISABLE_BILLING_SCAN } = useFeatureFlags();
    const isAdHocRelationship = relationshipModel.isAdHoc(agreement);
    const overlayContext = useOverlayContext();
    const { t } = useLocaleKeys('billingHub');
    const lineItemIds = useRef();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const { attachments, openFilePicker, isUploading, handleRemove: handleRemoveAttachment, setCurrentAttachments } = useAttachFiles({
        fetchAttachmentUrl: (attachmentId)=>api.billing.scheduledBillingAttachments.getDownloadUrl(agreement.id, attachmentId),
        onFilesUploaded: async (uploadedData)=>{
            if (!selectedDate) return;
            await api.billing.scheduledBillingAttachments.add(agreement.id, selectedDate, uploadedData.map((att)=>({
                    id: att.id,
                    displayName: att.displayName
                })));
        },
        onRemove: async (attachment)=>{
            if (!selectedDate) return;
            await api.billing.scheduledBillingAttachments.remove(agreement.id, selectedDate, attachment.id);
        }
    });
    const { urlState: { [BillingHubUrlParams.SelectedDate]: urlSelectedDate }, changeUrlState } = useStateFromUrl({
        [BillingHubUrlParams.SelectedDate]: {
            defaultValue: invoiceDraftDates.dates()[0] ? formatNumericUSDate(invoiceDraftDates.dates()[0]) : '',
            applyDefaultOnlyOnMount: true
        }
    });
    useEffect(()=>{
        const date = new Date(urlSelectedDate);
        setSelectedDate(isNaN(date.getTime()) ? null : date);
    }, [
        urlSelectedDate
    ]);
    const { loading: loadingInvoiceDraft, refresh: refreshInvoiceDraft, renderRestfulState: renderScheduledInvoice } = useRestfulWrapper({
        fetch: fetchInvoiceDraft
    });
    const invoiceDateInfo = getInvoiceDateInfo(selectedDate, billingData.invoiceDraftDates);
    const isActiveAgreement = useMemo(()=>!agreement.effectiveDate || timeModel.fromDateStampObject(agreement.effectiveDate).getTime() <= getNow().getTime(), [
        agreement.effectiveDate
    ]);
    useSubscribeToBillingObservable(()=>refreshAllData({
            silent: true
        }));
    useEffect(()=>{
        const selectedDate = !urlSelectedDate && (invoiceDraftDates === null || invoiceDraftDates === void 0 ? void 0 : invoiceDraftDates.dates().length) ? formatNumericUSDate(invoiceDraftDates.dates()[0]) : urlSelectedDate && !(invoiceDraftDates === null || invoiceDraftDates === void 0 ? void 0 : invoiceDraftDates.dates().length) ? '' : urlSelectedDate;
        changeUrlState({
            selectedDate
        });
    }, [
        invoiceDraftDates
    ]);
    useDidUpdate(()=>{
        refreshInvoiceDraft();
    }, [
        selectedDate
    ]);
    async function fetchInvoiceDraft() {
        const getAddedLineItemId = (newInvoiceDraft)=>{
            // used for blink animation
            const newLineItemIds = newInvoiceDraft.lineItems.map((param)=>{
                let { id } = param;
                return id;
            });
            const newAddedLineItemId = lineItemIds.current ? first(difference(newLineItemIds, lineItemIds.current)) : undefined;
            lineItemIds.current = newLineItemIds;
            return newAddedLineItemId;
        };
        if (selectedDate) {
            var _ref;
            const newInvoiceDraft = await api.invoiceDraft.getByDate(agreement.id, selectedDate);
            // Update attachments in the hook with the new invoice draft's attachments
            setCurrentAttachments((_ref = newInvoiceDraft === null || newInvoiceDraft === void 0 ? void 0 : newInvoiceDraft.attachments.map((att)=>({
                    id: att.id,
                    displayName: att.displayName,
                    fileSize: att.fileSize
                }))) !== null && _ref !== void 0 ? _ref : []);
            return {
                invoiceDraft: newInvoiceDraft,
                addedLineItemId: newInvoiceDraft ? getAddedLineItemId(newInvoiceDraft) : undefined
            };
        } else {
            setCurrentAttachments([]);
            return {
                invoiceDraft: null
            };
        }
    }
    async function onApprove(chargeId) {
        logBI(10569);
        const loaderName = 'approveCharge';
        overlayContext.startLoader(loaderName);
        try {
            await api.charge.approve(agreement.id, chargeId);
            await refreshAllData();
            overlayContext.showSuccessToast(t.charges.approveSuccess(), 'approve-success-toast');
        } catch (err) {
            overlayContext.showErrorToast(t.charges.approveFailure(), 'approve-failure-toast');
        } finally{
            overlayContext.stopLoader(loaderName);
        }
    }
    async function onRemove(chargeId, options) {
        const { billLaterCommandId, serviceId } = options;
        const confirmed = await overlayContext.showModal({
            biName: 'billing_hub_remove_line_item_confirm',
            closeOnClickOutside: true,
            shouldBlurBackground: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'billing_hub_confirm'
                        },
                        secondaryButton: {
                            name: 'billing_hub_cancel'
                        }
                    },
                    skin: "danger",
                    title: t.removeLineItem.title(),
                    content: t.removeLineItem.content(),
                    primaryButtonLabel: t.removeLineItem.confirm(),
                    secondaryButtonLabel: t.removeLineItem.cancel(),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false)
                });
            }
        });
        if (!confirmed) return;
        const loaderName = 'removeCharge';
        overlayContext.startLoader(loaderName);
        const isService = !!serviceId;
        try {
            if (isService && billLaterCommandId) {
                logBI(10570);
                await api.billCommand.remove(agreement.id, serviceId, billLaterCommandId);
            } else {
                logBI(10567);
                await api.charge.delete(agreement.id, chargeId);
            }
            await refreshAllData();
            overlayContext.showSuccessToast(isService ? t.charges.removeServiceSuccess() : t.charges.removeSuccess(), 'remove-success-toast');
        } catch (err) {
            const e = new ErrorResponse(err);
            const errorText = e.hasServerError(ServerError.AdHocChargeNotFound) ? t.charges.removeNotFoundFailure() : isService ? t.charges.removeServiceFailure() : t.charges.removeFailure();
            overlayContext.showErrorToast(errorText, 'remove-failure-toast');
        } finally{
            overlayContext.stopLoader(loaderName);
        }
    }
    async function refreshAllData(options) {
        await Promise.all([
            refreshInvoiceDraft(options),
            refreshBillingHubData(options)
        ]);
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "billing-hub",
        children: [
            /*#__PURE__*/ _jsx(MinisiteTabHeader, {
                appcuesId: "billing-hub-header",
                text: invoiceDraftDates.dates().length > 0 && isVendor && /*#__PURE__*/ _jsx(InvoiceNavigator, {
                    value: selectedDate,
                    options: invoiceDraftDates.dates(),
                    onChange: (date)=>{
                        lineItemIds.current = undefined;
                        changeUrlState({
                            selectedDate: date ? formatNumericUSDate(date) : ''
                        });
                    }
                }),
                actions: isVendor && /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx("div", {
                        "data-testid": "billing-hub-actions",
                        "data-appcuesid": "billing-hub-actions",
                        style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        },
                        children: /*#__PURE__*/ _jsxs(Row, {
                            gap: 16,
                            children: [
                                /*#__PURE__*/ _jsx(AttachFiles, {
                                    attachments: attachments,
                                    openFilePicker: openFilePicker,
                                    isUploading: isUploading,
                                    disabled: !selectedDate
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'open_manage_billing_drawer'
                                    },
                                    skin: "primaryOutline",
                                    disabled: DISABLE_BILLING_SCAN || loadingInvoiceDraft || isAdHocRelationship,
                                    onClick: ()=>setIsDrawerOpen(true),
                                    testId: "manage-billing-button",
                                    tooltip: isAdHocRelationship ? {
                                        title: t.billingControlDisabledForAdHoc(),
                                        placement: 'top'
                                    } : DISABLE_BILLING_SCAN ? {
                                        title: /*#__PURE__*/ _jsx(DisabledBillingTooltip, {}),
                                        placement: 'left'
                                    } : undefined,
                                    children: t.billingControl()
                                })
                            ]
                        })
                    })
                })
            }),
            isVendor && !isActiveAgreement && /*#__PURE__*/ _jsx(Callout, {
                className: styles.callout,
                testId: "bh-future-agreement-warning",
                children: t.futureAgreementCallout({
                    date: timeModel.formatDate(agreement.effectiveDate)
                })
            }),
            /*#__PURE__*/ _jsx(Drawer, {
                open: isDrawerOpen,
                header: /*#__PURE__*/ _jsx(Typography, {
                    variant: "h6",
                    weight: "normal",
                    children: t.billingControl()
                }),
                footer: /*#__PURE__*/ _jsx(BillingControlFooter, {
                    agreement: agreement,
                    invoiceDateInfo: invoiceDateInfo,
                    earliestBillableDate: invoiceDraftDates.earliestBillableDate(),
                    controlData: billingControlData
                }),
                onClose: ()=>setIsDrawerOpen(false),
                onCloseButtonClick: ()=>setIsDrawerOpen(false),
                BackdropProps: {
                    invisible: true
                },
                children: /*#__PURE__*/ _jsx(BillingControl, {
                    agreement: agreement,
                    billingControlData: billingControlData,
                    invoiceDateInfo: invoiceDateInfo,
                    onRefresh: refreshAllData,
                    earliestBillableDate: invoiceDraftDates.earliestBillableDate()
                })
            }),
            renderScheduledInvoice({
                strategy: RestfulStrategy.PreferLoading,
                renderChild: (param)=>{
                    let { data } = param;
                    return data.invoiceDraft ? /*#__PURE__*/ _jsx(ScheduledInvoice, {
                        agreementId: agreement.id,
                        isVendor: isVendor,
                        issueDate: data.invoiceDraft.issueDate,
                        paymentDate: data.invoiceDraft.paymentDate,
                        rowsProps: getLineItemsProps(data.invoiceDraft, onApprove, onRemove, isVendor),
                        addedLineItemId: data.addedLineItemId,
                        totalAmount: data.invoiceDraft.totalAmount,
                        onRefresh: ()=>Promise.resolve(undefined),
                        attachments: attachments.length > 0 ? {
                            files: attachments,
                            onRemove: handleRemoveAttachment
                        } : undefined
                    }) : /*#__PURE__*/ _jsx(Typography, {
                        className: styles.empty,
                        testId: "no-invoices",
                        children: getNoInvoicesText(selectedDate, isVendor, t)
                    });
                },
                renderError: ()=>/*#__PURE__*/ _jsx(GenericError, {
                        testId: "error-boundary-page"
                    }),
                renderLoader: ()=>/*#__PURE__*/ _jsx("div", {
                        className: styles.loader,
                        "data-testid": "invoice-draft-loading",
                        children: /*#__PURE__*/ _jsx(Spinner, {})
                    })
            })
        ]
    });
}
export default function BillingHub(props) {
    const { agreement, isVendor, refreshAgreement } = props;
    const { api } = useStoreActions();
    const { t } = useLocaleKeys('billingHub');
    const emptyState = getEmptyState(agreement, t);
    const isXeroLinked = useStore(selectors.integration.showXeroIntegration);
    const isQboLinked = useStore(selectors.integration.showQboIntegration);
    const { refresh: fetchQboClasses } = useQboClasses();
    const { refresh: fetchQboItems } = useAccountingItems(Accounting.QBO, {
        enabled: isQboLinked
    });
    const { refresh: fetchXeroItems } = useAccountingItems(Accounting.Xero, {
        enabled: isXeroLinked
    });
    const { refresh: fetchQboTimeActivities } = useQboTimeActivities(agreement.id);
    useSetDocumentTitle(t.pageTitle());
    async function fetchBillingHubData() {
        const [invoiceDraftDates, billingControlData] = await Promise.all([
            api.invoiceDraft.getDates(agreement.id),
            isVendor ? api.relationship.getBillingControlData(agreement.id) : Promise.resolve(BillingControlDataModel.make()),
            isQboLinked && fetchQboItems(),
            isXeroLinked && fetchXeroItems(),
            isQboLinked && fetchQboClasses(),
            isQboLinked && fetchQboTimeActivities()
        ]);
        return {
            invoiceDraftDates,
            billingControlData
        };
    }
    return emptyState ? /*#__PURE__*/ _jsx(EmptyState, {
        testId: "billing-hub-empty-state",
        illustration: "UpcomingBill",
        title: emptyState.text,
        paragraphs: emptyState.paragraphs
    }) : /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetchBillingHubData,
        renderLoader: ()=>/*#__PURE__*/ _jsx(Spinner, {
                centeredInBox: true,
                centerBoxHeight: 300
            }),
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            return /*#__PURE__*/ _jsx(BillingHubWithData, {
                billingData: data,
                agreement: agreement,
                isVendor: isVendor,
                isRefreshing: loading,
                onRefresh: async (options)=>{
                    await Promise.all([
                        refresh(options),
                        refreshAgreement(options)
                    ]);
                }
            });
        }
    });
}
